import React, { useState, useEffect } from "react";
import axios from "axios";
import "./profile.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Nav from "../components/Nav";
import { clearSession, getSession } from "../utils/session";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sangitshreeprakashan.com/backend/api";

const PROFILE_URL = `${API_BASE}/profile.php`;
const DELETE_URL = `${API_BASE}/deleteuser.php`;
const LOGOUT_URL = `${API_BASE}/logout.php`;

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  /* ---------------- SESSION CHECK ---------------- */
  useEffect(() => {
    const session = getSession();

    if (!session.isLoggedIn) {
      handleForceLogout();
      return;
    }

    fetchProfile();

    const onStorage = () => {
      const s = getSession();
      if (!s.isLoggedIn) {
        handleForceLogout();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ---------------- FETCH PROFILE ---------------- */
  const fetchProfile = () => {
    axios
      .get(PROFILE_URL, { withCredentials: true })
      .then((res) => {

        if (res.data.status === "success") {
          setUser(res.data.user || null);
          setOrders(res.data.orders || []);
        } else {
          handleForceLogout();
        }

        setLoading(false);
      })
      .catch(() => {
        handleForceLogout();
      });
  };

  /* ---------------- FORCE LOGOUT ---------------- */
  const handleForceLogout = () => {
    clearSession();
    setUser(null);
    setOrders([]);
    navigate("/login", { replace: true });
  };

  /* ---------------- DELETE ACCOUNT ---------------- */
  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);

  const handleDelete = async () => {
    setDeleting(true);

    try {
      const payload = user?.id
        ? { id: user.id }
        : { email: user?.email || "" };

      const resp = await axios.post(DELETE_URL, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (resp.data?.success) {
        alert("Your account has been deleted.");

        try {
          await axios.post(LOGOUT_URL, {}, { withCredentials: true });
        } catch {}

        handleForceLogout();
      } else {
        alert(resp.data?.message || "Could not delete account.");
      }
    } catch {
      alert("Network error while deleting account.");
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  /* ---------------- UI ---------------- */
  if (loading) return <div className="profile-page">Loading profile...</div>;
  if (!user) return null;

  return (
    <>
      <Nav />
      <Navbar />

      <div className="profile-page">
        <h2>User Profile</h2>

        {/* ---------------- USER INFO ---------------- */}
        <div className="profile-top-row">
          <div className="profile-info">
            <p><strong>Name:</strong> {user.name || "N/A"}</p>
            <p><strong>Email:</strong> {user.email || "N/A"}</p>
            <p><strong>Phone:</strong> {user.phone || "N/A"}</p>
          </div>

          <div className="profile-actions">
            <button
              className="btn btn-danger delete-account-btn"
              onClick={openConfirm}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete Account"}
            </button>
          </div>
        </div>

        {/* ---------------- ORDERS ---------------- */}
        <h3>Your Orders</h3>

        {orders.length === 0 ? (
          <p>No orders found</p>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Items</th>
                <th>Tracking ID</th>
                <th>Track</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => {

                const items = Array.isArray(order.items)
                  ? order.items
                  : [];

                const itemNames = items
                  .map((i) => i.product_name || i.title || "")
                  .filter(Boolean)
                  .join(", ");

                return (
                  <tr key={order.id}>
                    <td>{order.razorpay_order_id || order.id}</td>

                    <td>{itemNames || "N/A"}</td>

                    <td>
                      {order.tracking_id || "Pending"}
                    </td>

                    <td>
                      {order.tracking_id ? (
<a
  href="https://www.indiapost.gov.in/"
  target="_blank"
  rel="noopener noreferrer"
  title={order.tracking_id}
>
  Track Here
</a>
                      ) : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------------- DELETE MODAL ---------------- */}
      {confirmOpen && (
        <div className="modal-overlay" onClick={closeConfirm}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h4>Confirm account deletion</h4>
            <p>This action cannot be undone.</p>

            <div className="modal-actions">
              <button onClick={closeConfirm}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, delete my account"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ProfilePage;